import { createElement, memo, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeftRight,
  CheckCircle2,
  Crown,
  Pencil,
  PauseCircle,
  UserMinus,
} from 'lucide-react';
import type { KpiComplexityLevel } from '@/shared/api';
import {
  CategoryIcon,
  CriticalPriorityIcon,
  TICKET_PRIORITY_LABELS,
  UncategorizedIcon,
  type TicketPriority,
} from '@/shared/config';
import type {
  TicketAssignee,
  TicketDetailData,
  TicketLegacy,
  TicketLegacyPerson,
  TicketParticipant,
  UserInfo,
} from '@/entities/ticket';
import { formatWorkMinutes, slaProgress, slaShortLabel, slaTone } from '@/entities/ticket';
import {
  Avatar,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Tooltip,
} from '@/shared/ui';
import { useTicketActionsContext } from './TicketActions';
import { ClassificationDialog } from './ClassificationDialog';
import { useAuthStore } from '@/features/auth';
import { categoriesApi } from '@/shared/api';
import { categoryIcon } from '@/entities/category';
import { cn } from '@/shared/lib/cn';
import { getUserDisplayName, slaReactionText, slaStateCaption } from './lib';
import { RequesterContacts } from './RequesterContacts';
import styles from './TicketDetail.module.css';

const PersonAvatar: React.FC<{ userId: string; name: string; src?: string | null }> = ({ userId, name, src }) => (
  <Avatar className={styles.personAvatar} name={name} seed={userId} src={src} />
);

const Fact: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'danger' | 'warning';
  title?: string;
  action?: React.ReactNode;
}> = ({ label, value, tone, title, action }) => (
  <div className={styles.fact} title={title}>
    <span className={styles.factLabel}>{label}</span>
    <span
      className={cn(
        styles.factValue,
        tone === 'danger' && styles.factValueDanger,
        tone === 'warning' && styles.factValueWarning,
      )}
    >
      {value}
    </span>
    {action && <span className={styles.factEdit}>{action}</span>}
  </div>
);


const CLASSIFICATION_LOCKED = new Set(['resolved', 'closed', 'withdrawn', 'rejected']);

export const ServiceLine: React.FC<{
  ticket: TicketDetailData;
  serviceInTitle?: boolean;
}> = ({ ticket, serviceInTitle = false }) => {
  const canEdit = useAuthStore((s) => s.hasPermission('rate_ticket_complexity'));
  const [open, setOpen] = useState(false);
  const category = ticket.snapshot?.category?.name;
  const service = ticket.snapshot?.subcategory?.name;
  const editable = canEdit && !CLASSIFICATION_LOCKED.has(ticket.status);
  const empty = !category && !service;
  const [iconName, setIconName] = useState<string | null>(null);
  useEffect(() => {
    if (!ticket.categoryId) return;
    let alive = true;
    categoriesApi
      .list()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        if (alive) setIconName(list.find((c) => c.id === ticket.categoryId)?.icon ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ticket.categoryId]);
  const body = (
    <>
      {empty ? (
        <UncategorizedIcon size={14} aria-hidden className={styles.serviceIcon} />
      ) : (
        createElement(iconName ? categoryIcon(iconName) : CategoryIcon, {
          size: 14,
          'aria-hidden': true,
          className: styles.serviceIcon,
        })
      )}
      <span className={styles.serviceText}>
        {empty ? (
          'Услуга не указана'
        ) : (
          <>
            {category && service && serviceInTitle ? (
              <span className={styles.serviceName}>{category}</span>
            ) : (
              <>
                <span className={styles.serviceName}>{service ?? category}</span>
                {category && service && (
                  <span className={styles.serviceHead}>
                    {category}
                    <span className={styles.serviceSep} aria-hidden>
                      ›
                    </span>
                  </span>
                )}
              </>
            )}
          </>
        )}
      </span>
      {editable && <ArrowLeftRight size={13} aria-hidden className={styles.serviceEdit} />}
    </>
  );
  if (!editable) {
    return (
      <span className={styles.serviceLine} title={empty ? undefined : [category, service].filter(Boolean).join(' › ')}>
        {body}
      </span>
    );
  }
  return (
    <>
      <Tooltip content="Изменить категорию и услугу — KPI и SLA пересчитаются">
        <button
          type="button"
          className={cn(styles.serviceLine, styles.serviceLineButton)}
          aria-label={`Услуга: ${empty ? 'не указана' : [category, service].filter(Boolean).join(' › ')}. Изменить`}
          onClick={() => setOpen(true)}
        >
          {body}
        </button>
      </Tooltip>
      <ClassificationDialog
        ticketId={ticket.id}
        open={open}
        categoryId={ticket.categoryId ?? null}
        subcategoryId={ticket.subcategoryId ?? null}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

function formatDueShort(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  const year = d.getFullYear() === new Date().getFullYear() ? '' : `.${p(d.getFullYear() % 100)}`;
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}${year} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const PriorityMark: React.FC<{ priority: TicketPriority }> = ({ priority }) => (
  <span className={cn(styles.prio, styles[`prio-${priority}`])} aria-hidden>
    {priority === 'critical' ? (
      <CriticalPriorityIcon size={15} strokeWidth={2.25} className={styles.prioFlame} />
    ) : (
      <>
        <span className={styles.prioBar} />
        <span className={styles.prioBar} />
        <span className={styles.prioBar} />
      </>
    )}
  </span>
);

const ReactionMark: React.FC<{ breached: boolean; size: number; className?: string }> = ({ breached, size, className }) =>
  breached ? (
    <AlertCircle size={size} aria-hidden className={cn(styles.factsReactionBad, className)} />
  ) : (
    <CheckCircle2 size={size} aria-hidden className={cn(styles.factsReactionOk, className)} />
  );

const ComplexitySegment: React.FC<{ level: KpiComplexityLevel | undefined; complexityClass: number }> = ({
  level,
  complexityClass,
}) => {
  const ctx = useTicketActionsContext();
  const options = (ctx?.actions ?? []).filter((a) => a.key.startsWith('complexity:'));
  const body = (
    <>
      <span className={styles.factsSegValue}>
        {level?.label ?? `класс ${complexityClass}`}
        <span className={styles.factsPillMuted}>×{level?.multiplier ?? 1}</span>
      </span>
      <span className={styles.factsSegCaption}>
        сложность
        {options.length > 0 && <Pencil size={10} aria-hidden />}
      </span>
    </>
  );
  if (options.length === 0) return <div className={styles.factsSeg}>{body}</div>;
  const request = options[0].label.startsWith('Запросить');
  return (
    <Menu>
      <MenuTrigger
        render={
          <button
            type="button"
            className={cn(styles.factsSeg, styles.slaSegButton)}
            title={request ? 'Запросить у старшего смену сложности' : 'Изменить сложность (KPI)'}
          />
        }
      >
        {body}
      </MenuTrigger>
      <MenuContent>
        {options.map((a) => (
          <MenuItem key={a.key} disabled={a.disabled} onClick={a.run}>
            {a.label.replace(/^(Запросить сложность|Сложность): /, '')}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
};

const SlaCardBase: React.FC<{
  ticket: TicketDetailData;
  bare?: boolean;
  isStaffViewer?: boolean;
  complexityLevels?: KpiComplexityLevel[];
}> = ({ ticket, bare, isStaffViewer = false, complexityLevels = [] }) => {
  const sla = ticket.sla;
  const reaction = ticket.slaReaction ?? null;
  const priority = ticket.priority as TicketPriority;
  const level = complexityLevels.find((l) => l.class === (ticket.complexityClass ?? 1));
  const showComplexity = isStaffViewer && complexityLevels.length > 0;
  if (!sla && !reaction && !isStaffViewer) return null;

  const tone = sla ? slaTone(sla) : null;
  const reactionText = slaReactionText(reaction);
  const tip = sla
    ? [
        `Израсходовано ${formatWorkMinutes(sla.spentMinutes)} из ${formatWorkMinutes(sla.targetMinutes)} рабочих`,
        reactionText,
      ]
        .filter(Boolean)
        .join('. ')
    : reactionText;

  const head = sla ? (
    <Tooltip content={tip}>
      <div
        className={cn(
          styles.slaTile,
          tone === 'paused' && styles.slaTilePaused,
          (tone === 'breached' || tone === 'burning') && styles.slaTileDanger,
          tone === 'warning' && styles.slaTileWarning,
        )}
        role="progressbar"
        tabIndex={0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(slaProgress(sla) * 100)}
        aria-label={sla.kind === 'reaction' ? 'Срок ответа' : 'Срок решения'}
      >
        <SlaHourglass progress={slaProgress(sla)} className={styles.slaTileGlass} />
        <span className={styles.slaRemaining}>
          {slaShortLabel(sla)}
          {sla.paused && <PauseCircle className={styles.slaPauseIcon} size={15} aria-label="На паузе" />}
        </span>
        <span className={styles.slaCaption}>
          {reaction && <ReactionMark breached={reaction.breached} size={11} className={styles.slaCaptionIcon} />}
          {slaStateCaption(sla)}
          {sla.dueAt && (
            <span className={styles.slaDue} title={new Date(sla.dueAt).toLocaleString('ru')}>
              {' · '}
              {formatDueShort(sla.dueAt)}
            </span>
          )}
        </span>
      </div>
    </Tooltip>
  ) : reaction ? (
    <div className={styles.slaTile} title={reactionText ?? undefined}>
      <ReactionMark breached={reaction.breached} size={22} className={styles.slaTileGlass} />
      <span className={styles.slaRemaining}>
        {reaction.spentMinutes != null ? formatWorkMinutes(reaction.spentMinutes) : 'дан'}
      </span>
      <span className={styles.slaCaption}>{reaction.breached ? 'ответ с опозданием' : 'ответ в срок'}</span>
    </div>
  ) : null;

  const weightTip =
    ticket.priorityWeight != null
      ? [
          `Вес ${ticket.priorityWeight} — важность для заявителя, по ней сортируется бэклог`,
          ...(ticket.weightFactors ?? []).map((f) => `${f.label}: ${f.value} ×${f.coefficient}`),
        ].join('\n')
      : null;
  const prioritySeg = (
    <>
      <span className={styles.factsSegValue}>
        <PriorityMark priority={priority} />
        {TICKET_PRIORITY_LABELS[priority]}
      </span>
      <span className={styles.factsSegCaption}>приоритет</span>
    </>
  );
  const facts = isStaffViewer && (
    <div className={styles.slaFacts}>
      {weightTip ? (
        <Tooltip content={weightTip}>
          <div className={styles.factsSeg} tabIndex={0}>
            {prioritySeg}
          </div>
        </Tooltip>
      ) : (
        <div className={styles.factsSeg}>{prioritySeg}</div>
      )}
      {showComplexity && <ComplexitySegment level={level} complexityClass={ticket.complexityClass ?? 1} />}
    </div>
  );

  return (
    <section className={bare ? styles.summarySection : styles.card} aria-label="Срок и приоритет">
      <div className={styles.slaPanel}>
        {head}
        {facts}
      </div>
    </section>
  );
};

function legacyPerson(p: TicketLegacyPerson | null | undefined): string | null {
  if (!p) return null;
  return p.phone ? `${p.name} · ${p.phone}` : p.name;
}

function legacyHours(l: TicketLegacy): string | null {
  const parts = [
    l.plannedHours != null && `план ${l.plannedHours}\u00a0ч`,
    l.actualHours != null && `факт ${l.actualHours}\u00a0ч`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function legacyChanges(l: TicketLegacy): string | null {
  const list = (l.changes ?? []).map((c) => {
    const number = String(c['Изменение_Номер'] ?? '').replace(/^0+/, '');
    const state = c['Изменение_Состояние'];
    return [number && `№${number}`, typeof state === 'string' && state].filter(Boolean).join(' — ');
  });
  return list.length ? list.join('\n') : null;
}

function legacySprints(l: TicketLegacy): string | null {
  const names = (l.sprints ?? [])
    .map((s) => s['Спринт_Название'])
    .filter((n): n is string => typeof n === 'string' && n.length > 0);
  return names.length ? names.join(', ') : null;
}

const LegacyCardBase: React.FC<{ legacy: TicketLegacy }> = ({ legacy }) => {
  const rows: Array<[string, string | null | undefined]> = [
    ['Номер', legacy.number],
    ['Статус', legacy.status],
    ['Услуга', [legacy.service, legacy.component, legacy.detail].filter(Boolean).join(' / ') || null],
    ['Группа', legacy.group],
    ['Организация', legacy.organization],
    ['Инициатор', legacyPerson(legacy.initiator)],
    ['Ответственный', legacyPerson(legacy.responsible)],
    ['Разработчик', legacyPerson(legacy.developer)],
    ['Аналитик', legacyPerson(legacy.analyst)],
    ['Трудозатраты', legacyHours(legacy)],
    ['Изменения', legacyChanges(legacy)],
    ['Спринты', legacySprints(legacy)],
    ['Решение', legacy.resolution],
  ];
  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle} title={legacy.title}>
        Из Итилиум
      </h2>
      <div className={styles.factGrid}>
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <Fact key={label} label={label} value={<span className={styles.legacyValue}>{value}</span>} />
          ))}
      </div>
    </div>
  );
};

const RequesterDetailsBase: React.FC<{
  ticket: TicketDetailData;
  isStaffViewer: boolean;
  isAgent: boolean;
}> = ({ ticket, isStaffViewer, isAgent }) => {
  const clientInfo = ticket.clientInfo as
    | { browser?: string; os?: string; ip?: string }
    | null
    | undefined;
  const device = isAgent && clientInfo
    ? [clientInfo.browser, clientInfo.os, clientInfo.ip].filter(Boolean).join(' · ')
    : '';
  const phone = isStaffViewer ? ticket.creatorPhone : null;
  const department = ticket.creator?.department;
  const email = isStaffViewer ? ticket.creator?.email : null;
  if (!phone && !email && !department && !device) return null;
  return (
    <>
      {(department || device) && (
        <div className={styles.personDetails}>
          {department && <span>{department}</span>}
          {device && <span title="Браузер, ОС и адрес, с которых подана заявка">{device}</span>}
        </div>
      )}
      <RequesterContacts phone={phone} email={email} className={styles.personContacts} />
    </>
  );
};

const PeopleCardBase: React.FC<{
  assignees: TicketAssignee[];
  participants: TicketParticipant[];
  creator?: UserInfo | null;
  creatorId?: string | null;
  canReassignAssignee: boolean;
  canRemoveAssignee: boolean;
  canViewProfiles: boolean;
  removingUserId: string | null;
  onRemoveAssignee: (userId: string) => void;
  onReassignAssignee: (assignee: TicketAssignee) => void;
  requesterDetails?: React.ReactNode;
  requesterVip?: boolean;
  bare?: boolean;
}> = ({
  assignees,
  participants,
  creator,
  creatorId,
  canReassignAssignee,
  canRemoveAssignee,
  canViewProfiles,
  removingUserId,
  onRemoveAssignee,
  onReassignAssignee,
  requesterDetails,
  requesterVip,
  bare,
}) => (
  <div className={bare ? styles.summarySection : styles.card}>
    {bare ? (
      <span className={styles.subLabel}>Люди</span>
    ) : (
      <h2 className={styles.cardTitle}>Люди</h2>
    )}
    <div className={bare ? styles.peopleInline : undefined}>
    {creatorId && (
      <div className={styles.personRow}>
        <VipAvatarFrame vip={requesterVip}>
          <PersonAvatar userId={creatorId} name={getUserDisplayName(creator, creatorId)} src={creator?.avatarUrl} />
        </VipAvatarFrame>
        <span className={styles.personName}>
          {canViewProfiles ? (
            <Link to={`/users/${creatorId}`} className={styles.profileLink}>
              {getUserDisplayName(creator, creatorId)}
            </Link>
          ) : (
            getUserDisplayName(creator, creatorId)
          )}
          {requesterVip && <VipPill />}
          <span className={styles.personRole}>заявитель</span>
        </span>
      </div>
    )}
    {creatorId && requesterDetails}
    {assignees.map((a) => (
      <div key={a.id} className={styles.personRow}>
        <PersonAvatar userId={a.userId} name={getUserDisplayName(a.user, a.userId)} src={a.user?.avatarUrl} />
        <span className={styles.personName}>
          {canViewProfiles ? (
            <Link to={`/users/${a.userId}`} className={styles.profileLink}>
              {getUserDisplayName(a.user, a.userId)}
            </Link>
          ) : (
            getUserDisplayName(a.user, a.userId)
          )}
          <span className={styles.personRole}>ответственный</span>
        </span>
        {(canReassignAssignee || canRemoveAssignee) && (
          <span className={styles.personActions}>
            {canReassignAssignee && (
              <Tooltip content="Переназначить другому">
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Переназначить другому"
                  onClick={() => onReassignAssignee(a)}
                  disabled={removingUserId === a.userId}
                >
                  <ArrowLeftRight size={14} aria-hidden />
                </button>
              </Tooltip>
            )}
            {canRemoveAssignee && (
              <Tooltip content="Снять ответственного">
                <button
                  type="button"
                  className={cn(styles.iconBtn, styles.iconBtnDanger)}
                  aria-label="Снять ответственного"
                  onClick={() => onRemoveAssignee(a.userId)}
                  disabled={removingUserId === a.userId}
                >
                  <UserMinus size={14} aria-hidden />
                </button>
              </Tooltip>
            )}
          </span>
        )}
      </div>
    ))}
    { }
    {participants.map((p) => (
      <div key={p.id} className={styles.personRow}>
        <PersonAvatar userId={p.userId} name={getUserDisplayName(p.user, p.userId)} src={p.user?.avatarUrl} />
        <span className={styles.personName}>
          {canViewProfiles ? (
            <Link to={`/users/${p.userId}`} className={styles.profileLink}>
              {getUserDisplayName(p.user, p.userId)}
            </Link>
          ) : (
            getUserDisplayName(p.user, p.userId)
          )}
          <span className={styles.personRole}>
            {p.kind === 'approver' ? 'согласующий' : 'участник чата'}
          </span>
        </span>
      </div>
    ))}
    </div>
  </div>
);


export const VipPill: React.FC = () => (
  <span
    className={styles.vipPill}
    title="Заявитель был VIP, когда подавал заявку: она стоит первой в списках, а о создании пришло обязательное уведомление"
  >
    VIP
  </span>
);

export const VipAvatarFrame: React.FC<{ vip?: boolean; children: React.ReactNode }> = ({ vip, children }) =>
  vip ? (
    <span className={styles.vipFrame}>
      {children}
      <Crown size={10} strokeWidth={2.5} className={styles.vipCrown} aria-hidden />
    </span>
  ) : (
    <>{children}</>
  );

export const SlaHourglass: React.FC<{ progress: number; className?: string }> = ({ progress, className }) => {
  const id = useId();
  const spent = Math.min(1, Math.max(0, progress));
  const left = 1 - spent;
  return (
    <svg viewBox="0 0 14 18" width={11} height={14} className={className} aria-hidden>
      { }
      <clipPath id={`${id}t`}>
        <rect x="0" y={9 - 7.4 * left} width="14" height={7.4 * left} />
      </clipPath>
      <clipPath id={`${id}b`}>
        <rect x="0" y={16.4 - 7.4 * spent} width="14" height={7.4 * spent} />
      </clipPath>
      <path d="M2.5 1.6h9v0.9L7 9 2.5 2.5z" fill="currentColor" clipPath={`url(#${id}t)`} />
      <path d="M7 9l4.5 6.5v0.9h-9v-0.9z" fill="currentColor" clipPath={`url(#${id}b)`} />
      <path
        d="M1.5 1h11M1.5 17h11M2.5 1v1.5L7 9l4.5-6.5V1M2.5 17v-1.5L7 9l4.5 6.5V17"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
export const RequesterDetails = memo(RequesterDetailsBase);
export const SlaCard = memo(SlaCardBase);
export const PeopleCard = memo(PeopleCardBase);
export const LegacyCard = memo(LegacyCardBase);
